import './productDetailsDesktop.scss';

import { useCallback } from 'react';
import { ButtonColor, ButtonVariant, Size, Skeleton } from '@eliberty/design-system-react';

import { BackButton, TextHTML } from '~atoms';
import { PurchaseCallToAction, SlopesOverview } from '~molecules';
import { ProductCategory } from '~types/appsync.types';

interface ProductDetailsDesktopProps {
  /** Product category related informations */
  productCategory?: ProductCategory;
  /** Whether the product category data is loading */
  isLoadingProductCategory?: boolean;
  /** Called when the "Back to results" button is clicked. */
  onBackButtonClick?: () => void;
  /** Called when the user clicks the purchase CTA to open the drawer */
  onOpenPurchaseDrawer?: () => void;
}

/**
 * Desktop layout of the product details widget.
 *
 * Layout: title → hero image (CTA sticky overlay) → details body.
 */
const ProductDetailsDesktop = ({
  productCategory,
  isLoadingProductCategory = false,
  onBackButtonClick,
  onOpenPurchaseDrawer,
}: ProductDetailsDesktopProps) => {
  const additionalInfo = productCategory?.additionalInfo;

  const purchaseCtaContent =
    additionalInfo?.ctaBlockTitle ||
    additionalInfo?.ctaBlockSubtitle ||
    additionalInfo?.ctaButtonLabel
      ? {
          buttonLabel: additionalInfo.ctaButtonLabel ?? undefined,
          subtitle: additionalInfo.ctaBlockSubtitle ?? undefined,
          title: additionalInfo.ctaBlockTitle ?? undefined,
        }
      : undefined;
  const handleCtaClick = useCallback(() => {
    onOpenPurchaseDrawer?.();
  }, [onOpenPurchaseDrawer]);

  if (isLoadingProductCategory) {
    return (
      <div className='ProductDetailsDesktop' data-testid='ProductDetailsDesktop'>
        {onBackButtonClick && (
          <BackButton
            variant={ButtonVariant.Outlined}
            size={Size.M}
            color={ButtonColor.Primary}
            onClick={onBackButtonClick}
          />
        )}
        <Skeleton data-testid='Skeleton' height='36px' width='40%' />
        <Skeleton data-testid='Skeleton' height='340px' width='100%' radius='8px' />
        <Skeleton data-testid='Skeleton' height='32px' width='50%' />
        <Skeleton data-testid='Skeleton' height='22px' width='80%' />
        <Skeleton data-testid='Skeleton' height='22px' width='70%' />
      </div>
    );
  }

  return (
    <div className='ProductDetailsDesktop' data-testid='ProductDetailsDesktop'>
      {onBackButtonClick && (
        <BackButton
          variant={ButtonVariant.Outlined}
          size={Size.M}
          color={ButtonColor.Primary}
          onClick={onBackButtonClick}
        />
      )}

      <h1 className='ProductDetailsDesktop_title'>{productCategory?.name}</h1>

      <div className='ProductDetailsDesktop_imageWrapper'>
        {additionalInfo?.image && (
          <img
            className='ProductDetailsDesktop_heroImage'
            src={additionalInfo.image}
            alt={productCategory?.name ?? ''}
          />
        )}

        <div className='ProductDetailsDesktop_cta'>
          <PurchaseCallToAction content={purchaseCtaContent} onClick={handleCtaClick} />
        </div>
      </div>

      {additionalInfo && (
        <div className='ProductDetailsDesktop_details'>
          <SlopesOverview
            numTotalSlopes={additionalInfo.numTotalSlopes}
            numSkiLift={additionalInfo.numSkiLift}
            numKmSlope={additionalInfo.numKmSlope}
            numGreenSlopes={additionalInfo.numGreenSlopes}
            numBlueSlopes={additionalInfo.numBlueSlopes}
            numRedSlopes={additionalInfo.numRedSlopes}
            numBlackSlopes={additionalInfo.numBlackSlopes}
          />
          {additionalInfo.description && <TextHTML text={additionalInfo.description} />}
        </div>
      )}
    </div>
  );
};

export default ProductDetailsDesktop;
